import React, { useEffect, useState } from 'react';
import { getAuthToken, handleAuthTokenError, BASEURI } from '../utils';
import { useIntl } from 'react-intl';
import ClickAwayListener from 'react-click-away-listener';

export default function ResampleDropdown(props) {
    const intl = useIntl();
    const [resampleOptions, setResampleOptions] = useState({
        resample: null,
        resample_fn: null,
        reset_on: '01/01' // default reset date for yearly
    });

    const resample = {
        'H': intl.formatMessage({ id: 'app.chart.etcRawForm.hourly', defaultMessage: 'Hourly' }),
        'D': intl.formatMessage({ id: 'app.chart.etcRawForm.daily', defaultMessage: 'Daily' }),
        '7D': intl.formatMessage({ id: 'app.chart.etcRawForm.7Day', defaultMessage: '7-Day' }),
        'W': intl.formatMessage({ id: 'app.chart.etcRawForm.weekly', defaultMessage: 'Weekly' }),
        '15D': intl.formatMessage({ id: 'app.chart.etcRawForm.15Day', defaultMessage: '15-Day' }),
        '2W': intl.formatMessage({ id: 'app.chart.etcRawForm.biWeekly', defaultMessage: 'Bi-Weekly' }),
        'M': intl.formatMessage({ id: 'app.chart.etcRawForm.monthly', defaultMessage: 'Monthly' }),
        'Y': intl.formatMessage({ id: 'app.chart.etcRawForm.yearly', defaultMessage: 'Yearly' })
    }

    // Auto-apply on function select except for yearly (Y)
    useEffect(() => {
        if (resampleOptions.resample_fn && resampleOptions.resample !== 'Y') {
            resampleChart();
        }
    }, [resampleOptions.resample_fn, resampleOptions.resample]);

    // Ensure default reset date exists when switching to yearly
    useEffect(() => {
        if (resampleOptions.resample === 'Y' && !resampleOptions.reset_on) {
            setResampleOptions(prev => ({ ...prev, reset_on: '01/01' }));
        }
    }, [resampleOptions.resample]);

    function isValidReset(on) {
        // Accept M/D or MM/DD
        if (!on) return false;
        const m = on.split('/');
        if (m.length !== 2) return false;
        const mm = parseInt(m[0], 10);
        const dd = parseInt(m[1], 10);
        return /^\d{1,2}\/\d{1,2}$/.test(on) && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31;
    }

    function resampleChart() {
        const resampleFunction = resampleOptions.resample_fn;
        const freq = resampleOptions.resample;
        if (!resampleFunction || !freq) return;

        props.setLoading(true);

        let url = `${BASEURI}/api/chart/resample/${props.chartId}/freq/${freq}/fn/${resampleFunction}/`;
        // Only send reset_on when yearly and user provided a valid reset date
        if (freq === 'Y' && resampleOptions.reset_on && isValidReset(resampleOptions.reset_on)) {
            const q = `reset_on=${encodeURIComponent(resampleOptions.reset_on)}`;
            url = url.includes('?') ? `${url}&${q}` : `${url}?${q}`;
        }

        const options = {
            method: 'GET',
            headers: {
                "Content-Type": "application/json; charset=utf-8",
            },
        };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                options.headers.Authorization = `Bearer  ${token}`;
                props.toggleResampleDropdown();

                fetch(url, options)
                    .then((response) => response.json())
                    .then((graphs_data) => {
                        props.setLoading(false);
                        props.updateGraphs(graphs_data);
                    })
                    .catch(error => console.error('Error:', error));
            });
    }

    function handleChange(name, value) {
        setResampleOptions({ ...resampleOptions, [name]: value });
    }

    // Highlight helper for function list when yearly is selected
    const fnClass = (name) => {
        const base = "p-2 pl-4 hover:bg-gray-100 cursor-pointer";
        const selected = resampleOptions.resample === 'Y' && resampleOptions.resample_fn === name;
        return selected ? `${base} bg-emerald-50 text-emerald-700 font-medium` : base;
    };

    console.log('The resample', resampleOptions.resample)
    return (
        <ClickAwayListener onClickAway={props.toggleResampleDropdown}>
            {resampleOptions.resample ?
                <ul className="absolute right-0 min-w-[240px] drop-shadow-lg bg-white z-50">
                    <li className="p-2 pl-4 font-medium border-b-[1px] border-b-gray-100">
                        <div className='whitespace-nowrap flex justify-between items-center gap-2'>
                            <button
                                className="pl-0 text-emerald-400"
                                onClick={() => { handleChange('resample', null); }}
                            >
                                {resample[resampleOptions.resample]}
                            </button>
                        </div>
                    </li>
                    {/* Standard resample functions (highlight only for yearly) */}
                    <li className={fnClass('sum')} onClick={() => handleChange('resample_fn', 'sum')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.sum', defaultMessage: 'Sum' })}
                    </li>
                    <li className={fnClass('avg')} onClick={() => handleChange('resample_fn', 'avg')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.Mean', defaultMessage: 'Mean' })}
                    </li>
                    <li className={fnClass('min')} onClick={() => handleChange('resample_fn', 'min')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.min', defaultMessage: 'Min' })}
                    </li>
                    <li className={fnClass('max')} onClick={() => handleChange('resample_fn', 'max')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.max', defaultMessage: 'Max' })}
                    </li>
                    <li className={fnClass('diff')} onClick={() => handleChange('resample_fn', 'diff')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.diff', defaultMessage: 'Diff' })}
                    </li>

                    {/* Yearly optional reset date */}
                    {resampleOptions.resample === 'Y' && (
                        <li className="p-3 pl-4 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600">
                                    {intl.formatMessage({ id: 'app.chart.etcRawForm.resetOn', defaultMessage: 'Reset On (MM/DD)' })}
                                </label>
                                <input
                                    type="text"
                                    className="k-textbox !w-28 border border-gray-300 rounded-md"
                                    placeholder="MM/DD"
                                    value={resampleOptions.reset_on}
                                    onChange={(e) => handleChange('reset_on', e.target.value)}
                                />

                            </div>
                            {!isValidReset(resampleOptions.reset_on) && resampleOptions.reset_on && (
                                <div className="text-xs text-red-500 mt-1">
                                    {intl.formatMessage({ id: 'app.chart.etcRawForm.invalidReset', defaultMessage: 'Use MM/DD format, e.g., 01/01' })}
                                </div>
                            )}
                            <button
                                className="mdl-button mdl-js-button mdl-button--primary p-0"
                                disabled={!(isValidReset(resampleOptions.reset_on) && resampleOptions.resample_fn)}
                                onClick={resampleChart}
                            >
                                {intl.formatMessage({ id: 'app.chart.etcRawForm.apply', defaultMessage: 'Apply' })}
                            </button>
                        </li>
                    )}
                </ul>
                :
                <ul className="absolute right-0 min-w-[150px]  drop-shadow-lg bg-white z-50">
                    <li className="p-2 pl-4 font-medium border-b-[1px] border-b-gray-100">
                        <div className='whitespace-nowrap'>
                            <button className="text-gray-500 pl-0">
                                {intl.formatMessage({ id: 'app.chart.resample', defaultMessage: 'Resample' })}
                            </button>
                        </div>
                    </li>
                    <li className="p-2  pl-4 hover:bg-gray-100 cursor-pointer" onClick={() => handleChange('resample', 'H')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.hourly', defaultMessage: 'Hourly' })}
                    </li>
                    <li className="p-2  pl-4 hover:bg-gray-100 cursor-pointer" onClick={() => handleChange('resample', 'D')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.daily', defaultMessage: 'Daily' })}
                    </li>
                    <li className="p-2  pl-4 hover:bg-gray-100 cursor-pointer" onClick={() => handleChange('resample', '7D')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.7Day', defaultMessage: '7-Day' })}
                    </li>
                    <li className="p-2  pl-4 hover:bg-gray-100 cursor-pointer" onClick={() => handleChange('resample', 'W')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.weekly', defaultMessage: 'Weekly' })}
                    </li>
                    <li className="p-2  pl-4 hover:bg-gray-100 cursor-pointer" onClick={() => handleChange('resample', '15D')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.15Day', defaultMessage: '15-Day' })}
                    </li>
                    <li className="p-2  pl-4 hover:bg-gray-100 cursor-pointer" onClick={() => handleChange('resample', '2W')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.biWeekly', defaultMessage: 'Bi-Weekly' })}
                    </li>
                    <li className="p-2  pl-4 hover:bg-gray-100 cursor-pointer" onClick={() => handleChange('resample', 'M')}>
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.monthly', defaultMessage: 'Monthly' })}
                    </li>
                    <li
                        className="p-2  pl-4 hover:bg-gray-100 cursor-pointer"
                        onClick={() => { handleChange('resample', 'Y'); if (!resampleOptions.reset_on) handleChange('reset_on', '01/01'); }}
                    >
                        {intl.formatMessage({ id: 'app.chart.etcRawForm.yearly', defaultMessage: 'Yearly' })}
                    </li>
                </ul>
            }
        </ClickAwayListener>
    )
}